import { EARLY_SOLVER_BONUS, EARLY_SOLVER_COUNT, SOLVE_POINTS } from "./constants";
import type { LeaderboardEntry, LetterStatus, WordleResultRow } from "./types";

/**
 * Wordle feedback for one guess, handling repeated letters the way the original game does:
 * exact matches claim their letter first, then remaining letters are matched left to right.
 * @param guess The guessed word (any case).
 * @param answer The answer (any case), same length as the guess.
 * @returns One status per letter.
 */
export function scoreGuess(guess: string, answer: string): LetterStatus[] {
  const g = guess.toUpperCase().split("");
  const a = answer.toUpperCase().split("");
  const statuses: LetterStatus[] = g.map((letter, i) => (letter === a[i] ? "correct" : "absent"));

  const unmatched = new Map<string, number>();
  a.forEach((letter, i) => {
    if (statuses[i] === "correct") return;
    unmatched.set(letter, (unmatched.get(letter) ?? 0) + 1);
  });

  g.forEach((letter, i) => {
    if (statuses[i] === "correct") return;
    const remaining = unmatched.get(letter) ?? 0;
    if (remaining === 0) return;
    statuses[i] = "present";
    unmatched.set(letter, remaining - 1);
  });

  return statuses;
}

/**
 * Case- and whitespace-insensitive key used to treat "Anna Sun" and " anna  sun" as one player.
 * @param name Display name.
 * @returns Normalized key.
 */
export function playerKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/**
 * Rank a day's results. Each player's first result counts; the first {@link EARLY_SOLVER_COUNT}
 * solvers (by finish time) earn the early bonus. Solvers rank by points, then fewer tries, then
 * faster time; non-solvers follow unranked.
 * @param rows The day's results in any order.
 * @returns Ranked entries, solvers first.
 */
export function buildLeaderboard(rows: WordleResultRow[]): LeaderboardEntry[] {
  const chronological = [...rows].sort((x, y) => x.createdAt.localeCompare(y.createdAt));

  const seen = new Set<string>();
  const firstAttempts = chronological.filter((row) => {
    const key = playerKey(row.name);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  let solversSoFar = 0;
  const entries = firstAttempts.map((row) => {
    if (!row.won) {
      return { ...row, rank: null, points: 0, earlySolver: false };
    }
    solversSoFar += 1;
    const earlySolver = solversSoFar <= EARLY_SOLVER_COUNT;
    return {
      ...row,
      rank: null,
      points: SOLVE_POINTS + (earlySolver ? EARLY_SOLVER_BONUS : 0),
      earlySolver,
    };
  });

  const solvers = entries
    .filter((e) => e.won)
    .sort((x, y) => y.points - x.points || x.tries - y.tries || x.timeSeconds - y.timeSeconds)
    .map((e, i) => ({ ...e, rank: i + 1 }));
  const others = entries.filter((e) => !e.won);

  return [...solvers, ...others].map(({ name, tries, timeSeconds, won, points, earlySolver, rank }) => ({
    rank,
    name,
    tries,
    timeSeconds,
    won,
    points,
    earlySolver,
  }));
}

/**
 * Format seconds as m:ss.
 * @param totalSeconds Elapsed seconds.
 * @returns e.g. "2:05".
 */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
